import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';

/**
 * What every arena builder after the subway kept writing out again.
 *
 * The subway grew its own `box` and `material` helpers inside its builder, and
 * the next three arenas would each have grown the same two again, plus a
 * lighting rig read out of JSON. They live here instead. The subway's builder
 * is left as it is — it works, it is tested, and nothing about it is wrong.
 *
 * The rule from `subwayArena.js` holds for anything built with this: geometry
 * comes out of the arena's own numbers, and every material, geometry and
 * texture made goes on the kit's list so one `dispose()` gives all of it back.
 */

export function createKit(group) {
  const disposables = [];
  const keep = (item) => {
    disposables.push(item);
    return item;
  };

  function material(options) {
    return keep(new THREE.MeshStandardMaterial(options));
  }

  /** A mesh, placed, shadowed, and added to the arena. */
  function place(geometry, mat, { x = 0, y = 0, z = 0, rotY = 0, rotX = 0, rotZ = 0, name, casts = true, receives = true, parent = group } = {}) {
    keep(geometry);
    const mesh = new THREE.Mesh(geometry, mat);
    mesh.position.set(x, y, z);
    mesh.rotation.set(rotX, rotY, rotZ);
    if (name) mesh.name = name;
    mesh.castShadow = casts;
    mesh.receiveShadow = receives;
    parent.add(mesh);
    return mesh;
  }

  return {
    keep,
    material,
    place,
    box: (width, height, depth, mat, options) =>
      place(new THREE.BoxGeometry(width, height, depth), mat, options),
    cylinder: (radius, height, mat, { segments = 16, top = radius, ...options } = {}) =>
      place(new THREE.CylinderGeometry(top, radius, height, segments), mat, options),
    /**
     * One draw call per material for everything under `root` that never moves
     * or changes.
     *
     * A room drawn from primitives is a hundred meshes, and each is a draw
     * call: the garage's first build was 107 against the subway's 23. Merging
     * by material bakes each mesh's transform into one geometry, so the
     * scenery costs what its materials cost, not what its parts do. `exclude`
     * names what must stay its own mesh — a bonnet that dents, glass that is
     * swapped when it breaks. A material shared by a merged batch still
     * changes for all of it, which is how a car's indicators all flash.
     */
    mergeStatic(root, { exclude = () => false } = {}) {
      root.updateMatrixWorld(true);
      const toRoot = new THREE.Matrix4().copy(root.matrixWorld).invert();
      const batches = new Map();
      root.traverse((mesh) => {
        if (!mesh.isMesh || mesh.isInstancedMesh || mesh.isSkinnedMesh) return;
        if (mesh.children.length || exclude(mesh) || Array.isArray(mesh.material)) return;
        const key = `${mesh.material.uuid}:${mesh.castShadow}:${mesh.receiveShadow}`;
        if (!batches.has(key)) batches.set(key, []);
        batches.get(key).push(mesh);
      });
      let merged = 0;
      for (const meshes of batches.values()) {
        if (meshes.length < 2) continue;
        let parts = meshes.map((mesh) => {
          const part = mesh.geometry.clone();
          part.applyMatrix4(new THREE.Matrix4().multiplyMatrices(toRoot, mesh.matrixWorld));
          for (const name of Object.keys(part.attributes)) {
            if (!['position', 'normal', 'uv'].includes(name)) part.deleteAttribute(name);
          }
          return part;
        });
        // All indexed or none: the merge refuses a mixture.
        if (!parts.every((part) => part.index)) {
          parts = parts.map((part) => (part.index ? part.toNonIndexed() : part));
        }
        const geometry = mergeGeometries(parts);
        for (const part of parts) part.dispose();
        if (!geometry) continue;
        keep(geometry);
        const batch = new THREE.Mesh(geometry, meshes[0].material);
        batch.name = `merged-${meshes[0].name || 'static'}`;
        batch.castShadow = meshes[0].castShadow;
        batch.receiveShadow = meshes[0].receiveShadow;
        root.add(batch);
        for (const mesh of meshes) mesh.removeFromParent();
        merged += meshes.length;
      }
      return merged;
    },
    dispose() {
      for (const item of disposables) item.dispose?.();
      disposables.length = 0;
    },
  };
}

/**
 * A texture painted onto a canvas rather than loaded — the same answer the
 * subway's tiles gave, for the same reason: no file to fetch, and the size of
 * the pattern is a number in the arena's data.
 *
 * `paint(ctx, width, height, random)` draws it. `random` is seeded, so a
 * concrete stain or a lit window is in the same place on every load. Returns
 * null where there is no canvas — the unit suite runs in `node` — and every
 * caller treats that as "use the flat colour".
 */
export function drawnTexture(width, height, paint, { seed = 1, repeat = null, srgb = true } = {}) {
  if (typeof document === 'undefined' || !document.createElement) return null;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  paint(ctx, width, height, seeded(seed));
  const texture = new THREE.CanvasTexture(canvas);
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  if (srgb) texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = 8;
  if (repeat) texture.repeat.set(repeat[0], repeat[1]);
  return texture;
}

/**
 * A colour texture and a roughness texture painted from one layout (step 98).
 *
 * The rooms were flat colours lit by lamps, and a flat colour under a lamp is
 * a flat colour with a gradient on it. What makes a floor read as tile or
 * concrete is the light catching it unevenly — a glazed tile shines and the
 * grout does not, an oil stain is slick on dull concrete — and that needs the
 * *same* tile, the *same* stain, in both maps. So `paint(colour, rough, w, h,
 * random)` draws both canvases at once with one seeded generator: whatever it
 * puts in one it can put in the other, in the same place.
 *
 * The roughness canvas is grey: 255 is matt, 0 a mirror; three reads its
 * green channel and multiplies it by the material's own `roughness`, so a
 * material using it is given `roughness: 1`. Not colour-managed — it is a
 * number, not a colour. Both are null where there is no canvas.
 */
export function drawnMaps(width, height, paint, { seed = 1, repeat = null } = {}) {
  if (typeof document === 'undefined' || !document.createElement) return { map: null, roughnessMap: null };
  const make = () => {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    return canvas;
  };
  const colour = make();
  const rough = make();
  const c = colour.getContext('2d');
  const r = rough.getContext('2d');
  if (!c || !r) return { map: null, roughnessMap: null };
  paint(c, r, width, height, seeded(seed));
  const texture = (canvas, srgb) => {
    const t = new THREE.CanvasTexture(canvas);
    t.wrapS = THREE.RepeatWrapping;
    t.wrapT = THREE.RepeatWrapping;
    if (srgb) t.colorSpace = THREE.SRGBColorSpace;
    t.anisotropy = 8;
    if (repeat) t.repeat.set(repeat[0], repeat[1]);
    return t;
  };
  return { map: texture(colour, true), roughnessMap: texture(rough, false) };
}

/** A grey for a roughness canvas: 0 a mirror, 1 matt. */
export const grey = (value) => {
  const v = Math.round(Math.min(1, Math.max(0, value)) * 255);
  return `rgb(${v},${v},${v})`;
};

/** A colour scaled by `shade`, as a canvas fill. */
export function shadeOf(color, shade) {
  return `#${new THREE.Color(color).multiplyScalar(shade).getHexString()}`;
}

/**
 * Brick with weather on it (step 98): each brick its own shade, mortar
 * recessed and dull, the faces a little smoother where the rain has worn them,
 * and soot run down from the top. Two metres square — 26 courses of 77 mm,
 * nine bricks across — so the streaks do not repeat every metre up a wall.
 */
export function weatheredBrick({ brick = '#7b4a3c', mortar = '#4f3a33', seed = 11 } = {}) {
  return drawnMaps(512, 512, (c, r, w, h, random) => {
    c.fillStyle = mortar;
    c.fillRect(0, 0, w, h);
    r.fillStyle = grey(1);
    r.fillRect(0, 0, w, h);
    const rows = 26;
    const rowH = h / rows;
    const brickW = w / 9;
    for (let row = 0; row < rows; row++) {
      const shift = row % 2 ? brickW / 2 : 0;
      for (let col = -1; col < 10; col++) {
        const shade = 0.72 + random() * 0.42;
        const x = col * brickW + shift + 1;
        const y = row * rowH + 1;
        c.fillStyle = shadeOf(brick, shade);
        c.fillRect(x, y, brickW - 2, rowH - 2);
        // A darker, burnt header here and there, as old brick has.
        if (random() < 0.08) {
          c.fillStyle = 'rgba(20,10,8,0.35)';
          c.fillRect(x, y, brickW - 2, rowH - 2);
        }
        r.fillStyle = grey(0.7 + random() * 0.22);
        r.fillRect(x, y, brickW - 2, rowH - 2);
      }
    }
    // Soot and rain run down from the top in streaks.
    for (let i = 0; i < 30; i++) {
      const x = random() * w;
      const width = 3 + random() * 18;
      const length = h * (0.25 + random() * 0.75);
      const streak = c.createLinearGradient(0, 0, 0, length);
      streak.addColorStop(0, 'rgba(15,12,14,0.32)');
      streak.addColorStop(1, 'rgba(15,12,14,0)');
      c.fillStyle = streak;
      c.fillRect(x, 0, width, length);
      r.fillStyle = 'rgba(80,80,80,0.25)';
      r.fillRect(x, 0, width, length * 0.6);
    }
  }, { seed });
}

/** Brick, one metre square of it: thirteen courses, half-bond. */
export function brickTexture({ brick = '#7b4a3c', mortar = '#4f3a33', seed = 11 } = {}) {
  return drawnTexture(128, 128, (ctx, w, h, random) => {
    ctx.fillStyle = mortar;
    ctx.fillRect(0, 0, w, h);
    const rows = 13;
    const rowH = h / rows;
    const brickW = w / 4.5;
    for (let r = 0; r < rows; r++) {
      const shift = r % 2 ? brickW / 2 : 0;
      for (let c = -1; c < 6; c++) {
        const shade = 0.82 + random() * 0.3;
        const base = new THREE.Color(brick).multiplyScalar(shade);
        ctx.fillStyle = `#${base.getHexString()}`;
        ctx.fillRect(c * brickW + shift + 1, r * rowH + 1, brickW - 2, rowH - 2);
      }
    }
  }, { seed });
}

/** A small deterministic generator. Same seed, same stains. */
export function seeded(seed = 1) {
  let state = (seed * 2654435761) >>> 0 || 1;
  return () => {
    state ^= state << 13;
    state ^= state >>> 17;
    state ^= state << 5;
    return ((state >>> 0) % 100000) / 100000;
  };
}

/**
 * The lights an arena declares, built.
 *
 *   ambient   { color, intensity }
 *   bounce    { sky, ground, intensity }           a hemisphere light
 *   key       { type: 'directional' | 'spot', color, intensity, position,
 *               target?, angle?, penumbra?, distance?, extent? }
 *             the one light that casts a shadow
 *   lamps     [{ color, intensity, distance, decay, x, y, z }]
 *
 * One shadow caster, always. A second is a second shadow pass over every
 * skinned vertex in the scene, and the frame budget is set by a phone.
 */
export function buildLighting(lighting = {}) {
  const lights = new THREE.Group();
  lights.name = 'arena-lights';

  if (lighting.ambient) {
    lights.add(new THREE.AmbientLight(new THREE.Color(lighting.ambient.color), lighting.ambient.intensity));
  }
  if (lighting.bounce) {
    lights.add(
      new THREE.HemisphereLight(
        new THREE.Color(lighting.bounce.sky),
        new THREE.Color(lighting.bounce.ground),
        lighting.bounce.intensity
      )
    );
  }

  const key = lighting.key;
  if (key) {
    const color = new THREE.Color(key.color);
    const light =
      key.type === 'spot'
        ? new THREE.SpotLight(color, key.intensity, key.distance ?? 0, key.angle ?? 0.9, key.penumbra ?? 0.5, key.decay ?? 2)
        : new THREE.DirectionalLight(color, key.intensity);
    light.position.set(key.position.x, key.position.y, key.position.z);
    const aim = key.target ?? { x: 0, y: 0, z: 0 };
    light.target.position.set(aim.x, aim.y ?? 0, aim.z);
    lights.add(light.target);
    light.castShadow = true;
    light.shadow.mapSize.set(2048, 2048);
    light.shadow.bias = -0.0005;
    if (light.isDirectionalLight) {
      const extent = key.extent ?? 10;
      Object.assign(light.shadow.camera, { near: 1, far: 40, left: -extent, right: extent, top: extent, bottom: -extent });
    } else {
      light.shadow.camera.near = 0.5;
      light.shadow.camera.far = 20;
    }
    lights.add(light);
  }

  for (const lamp of lighting.lamps ?? []) {
    const light = new THREE.PointLight(new THREE.Color(lamp.color), lamp.intensity, lamp.distance ?? 10, lamp.decay ?? 2);
    light.position.set(lamp.x, lamp.y, lamp.z);
    lights.add(light);
  }
  return lights;
}

/** The arena-shaped object every builder returns, with the parts that never differ filled in. */
export function arenaShape(config, { group, kit, extra = {}, update, dispose, reset, onStruck }) {
  return {
    id: config.id,
    name: config.name,
    object3D: group,
    surfaces: config.surfaces,
    bounds: config.bounds,
    spawns: config.spawns,
    camera: config.camera ?? {},
    pit: config.pit ?? null,
    signature: config.signature ?? null,
    crowd: null,
    config,
    ground: false,
    lit: true,
    ...extra,
    update: update ?? (() => {}),
    ...(reset ? { reset } : {}),
    ...(onStruck ? { onStruck } : {}),
    dispose() {
      dispose?.();
      kit.dispose();
      group.removeFromParent();
    },
  };
}
